import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';
import { ConnectionEntity } from '../../../interfaces/collection.interface';
import { CollectionService } from '../../../services/collection.service';

// Selection context for dynamic text
type SelectionContext =
  | 'ecad' // PUBLIC_PERFORMANCE + EXECUTION (Sociedades de gestão coletiva)
  | 'streaming' // STREAMING revenue
  | 'mechanical' // MECHANICAL revenue
  | 'default'; // Fallback for unknown combinations

@Component({
  selector: 'lib-entity-selection-step',
  imports: [CommonModule, TranslocoModule, MzicSvgComponent],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './entity-selection-step.component.html',
  styleUrl: './entity-selection-step.component.scss',
})
export class EntitySelectionStepComponent implements OnInit {
  private readonly collectionService = inject(CollectionService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() revenueType = '';
  @Input({ required: true }) collectionId = '';
  @Input() revenueSubtype: string | null = null;
  @Input() userName = '';
  @Output() entitySelected = new EventEmitter<ConnectionEntity>();
  @Output() back = new EventEmitter<void>();

  entities = signal<ConnectionEntity[]>([]);
  selectedEntity = signal<ConnectionEntity | null>(null);
  searchTerm = signal('');
  loading = signal(true);
  error = signal<string | null>(null);

  /**
   * Determines the selection context based on revenue type and subtype
   * This is used to show context-specific titles and descriptions
   */
  get selectionContext(): SelectionContext {
    const type = this.revenueType?.toUpperCase();
    const subtype = this.revenueSubtype?.toUpperCase();

    // ECAD: Sociedades de gestão coletiva (collective management societies)
    if (type === 'PUBLIC_PERFORMANCE' && subtype === 'EXECUTION') {
      return 'ecad';
    }

    // Streaming platforms (distributors)
    if (type === 'STREAMING') {
      return 'streaming';
    }

    // Mechanical rights
    if (type === 'MECHANICAL' || subtype === 'MECHANICAL') {
      return 'mechanical';
    }

    return 'default';
  }

  /**
   * Returns the translation key suffix based on the selection context
   */
  get contextKeySuffix(): string {
    return this.selectionContext;
  }

  ngOnInit(): void {
    this.loadEntities();
  }

  private loadEntities(): void {
    this.loading.set(true);
    this.error.set(null);

    this.collectionService
      .getConnectionEntities(
        this.revenueType,
        this.revenueSubtype,
        this.collectionId,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entities) => {
          this.entities.set(entities);
          this.loading.set(false);
        },
        error: (err) => {
          console.error('Error loading entities:', err);
          this.error.set(
            'Failed to load connection entities. Please try again.',
          );
          this.loading.set(false);
        },
      });
  }

  get filteredEntities(): ConnectionEntity[] {
    const term = this.searchTerm().toLowerCase();
    if (!term) {
      return this.entities();
    }
    return this.entities().filter(
      (entity) =>
        entity.display_name.toLowerCase().includes(term) ||
        entity.name.toLowerCase().includes(term) ||
        entity.description?.toLowerCase().includes(term),
    );
  }

  selectEntity(entity: ConnectionEntity): void {
    this.selectedEntity.set(entity);
  }

  onContinue(): void {
    const selected = this.selectedEntity();
    if (selected) {
      // Waiver flow is now handled by collection-flow component
      // based on the require_waiver flag from the entity
      this.entitySelected.emit(selected);
    }
  }

  onBack(): void {
    this.back.emit();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
  }

  clearSearch(): void {
    this.searchTerm.set('');
  }

  retryLoad(): void {
    this.loadEntities();
  }

  get selectedEntityName(): string {
    return this.selectedEntity()?.display_name || '';
  }

  get shimmerItems(): number[] {
    return Array.from({ length: 4 }, (_, i) => i);
  }
}
